import React from 'react';
import styled, { keyframes } from 'styled-components';
import PropTypes from 'prop-types';
import { VerticalNav } from '../Nav';
import { MainLogoWhite } from '../MainLogo';
import dataNav from '../Navbar/data';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const primary = '#25a7c6';
const primaryLight = '#3dbdd9';
const primaryLighter = '#4ec8e3';
const white = '#fff';

const Drawer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1040;
  animation: ${fadeIn} 0.2s ease-out;
`;

const Backdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
`;

const Content = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${primary};
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.25s ease-out;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const LogoWrapper = styled.div`
  img {
    max-height: 32px;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${white};
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover,
  &:focus {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const NavWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 0;

  .nav {
    .nav-link {
      padding: 16px 24px;
      font-size: 1.1rem;
      color: ${white};
      background: ${primaryLight};
      border-radius: 0;
      border-top: 0;
      border-right: 0;
      border-bottom: 0;
      border-left: 7px solid ${primaryLight};
      margin: 0.2rem 0;
      font-weight: 400;
      width: 100%;

      &.active,
      &:hover,
      &:focus {
        color: ${white};
        font-weight: 700;
        border-left: 7px solid ${primaryLight};
        background: transparent;
      }
    }

    .dropdown {
      width: 100%;

      .nav-link {
        position: relative;

        &:after {
          right: 10px;
          position: absolute;
          top: calc(50% - 2px);
        }
      }

      &.show .nav-link {
        font-weight: 700;
        border-left: 7px solid ${primaryLight};
        background: ${primaryLighter};
        margin-bottom: 0;

        &.active,
        &:hover,
        &:focus {
          border-left: 7px solid ${primaryLight};
          background: ${primaryLighter};
        }
      }

      .dropdown-menu {
        position: relative !important;
        width: 100%;
        transform: none !important;
        background: ${primaryLighter};
        border: none;
        margin-bottom: 0.2rem;

        .dropdown-item {
          color: ${white};
          font-weight: 400;
          padding-left: 30px;
          white-space: normal;

          &.active,
          &:hover,
          &:focus {
            font-weight: 700;
            background: ${primaryLight};
          }
        }
      }
    }
  }
`;

const CloseIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const MobileDrawer = ({ isOpen, onClose, history, role }) => {
  if (!isOpen) return null;

  return (
    <Drawer>
      <Backdrop
        onClick={onClose}
        onKeyDown={onClose}
        role="button"
        tabIndex={0}
        aria-label="Cerrar menú"
      />
      <Content>
        <Header>
          <LogoWrapper>
            <MainLogoWhite />
          </LogoWrapper>
          <CloseButton type="button" onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </Header>
        <NavWrapper>
          <VerticalNav history={history} role={role} items={dataNav} />
        </NavWrapper>
      </Content>
    </Drawer>
  );
};

MobileDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  role: PropTypes.string
};

MobileDrawer.defaultProps = {
  role: ''
};

export default MobileDrawer;
